// web/src/hooks/useInactivityTimer.ts
// Auto sign-out after prolonged inactivity. Tracks mouse, keyboard, scroll, touch.
// Shows warning toast before timeout. Throttled to avoid excessive timer resets.

import { useEffect, useRef, useCallback } from 'react';

const THROTTLE_MS = 30_000; // Only reset timer every 30s max
const WARNING_BEFORE_MS = 5 * 60_000; // Show warning 5 min before timeout

export interface InactivityTimerOptions {
  timeoutMs: number;           // e.g. 30 * 60 * 1000 = 30 min
  onTimeout: () => void;       // Called when timeout fires (sign out)
  onWarning?: () => void;      // Called when warning fires (5 min left)
  enabled: boolean;            // Only active when authenticated
}

export function useInactivityTimer({
  timeoutMs,
  onTimeout,
  onWarning,
  enabled,
}: InactivityTimerOptions): void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastResetRef = useRef<number>(0);
  const onTimeoutRef = useRef(onTimeout);
  const onWarningRef = useRef(onWarning);

  // Keep callback refs fresh without re-running effect
  onTimeoutRef.current = onTimeout;
  onWarningRef.current = onWarning;

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
    if (warningRef.current) { clearTimeout(warningRef.current); warningRef.current = null; }
  }, []);

  const resetTimers = useCallback(() => {
    clearTimers();
    // Warning timer (fires 5 min before timeout)
    const warningDelay = timeoutMs - WARNING_BEFORE_MS;
    if (warningDelay > 0 && onWarningRef.current) {
      warningRef.current = setTimeout(() => {
        onWarningRef.current?.();
      }, warningDelay);
    }
    // Main timeout
    timeoutRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, timeoutMs);
  }, [timeoutMs, clearTimers]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastResetRef.current < THROTTLE_MS) return; // Throttle
    lastResetRef.current = now;
    resetTimers();
  }, [resetTimers]);

  useEffect(() => {
    if (!enabled) { clearTimers(); return; }

    // Start timers immediately
    lastResetRef.current = Date.now();
    resetTimers();

    const events: (keyof WindowEventMap)[] = [
      'mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click',
    ];
    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      clearTimers();
      events.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, [enabled, handleActivity, resetTimers, clearTimers]);
}
